import type { LatLng } from '../types/house'

const M_PER_DEG_LAT = 110540
const M_PER_DEG_LNG_EQ = 111320

function toXY([lat, lng]: LatLng, lat0: number): [number, number] {
  return [lng * Math.cos((lat0 * Math.PI) / 180) * M_PER_DEG_LNG_EQ, lat * M_PER_DEG_LAT]
}

export function ringArea(ring: LatLng[]): number {
  if (ring.length < 3) return 0
  const lat0 = ring[0][0]
  let sum = 0
  for (let i = 0; i < ring.length; i++) {
    const [x1, y1] = toXY(ring[i], lat0)
    const [x2, y2] = toXY(ring[(i + 1) % ring.length], lat0)
    sum += x1 * y2 - x2 * y1
  }
  return Math.abs(sum) / 2
}

export function pointInRing(point: LatLng, ring: LatLng[]): boolean {
  const [py, px] = point
  let inside = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [yi, xi] = ring[i]
    const [yj, xj] = ring[j]
    const intersect = yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi
    if (intersect) inside = !inside
  }
  return inside
}

export function centroid(ring: LatLng[]): LatLng {
  const lat = ring.reduce((s, p) => s + p[0], 0) / ring.length
  const lng = ring.reduce((s, p) => s + p[1], 0) / ring.length
  return [lat, lng]
}

export function distanceMeters(a: LatLng, b: LatLng): number {
  const [ax, ay] = toXY(a, a[0])
  const [bx, by] = toXY(b, a[0])
  return Math.hypot(ax - bx, ay - by)
}

export function ringsTouch(a: LatLng[], b: LatLng[], tolerance = 1.2): boolean {
  for (const p of a) {
    for (const q of b) {
      if (distanceMeters(p, q) < tolerance) return true
    }
  }
  return false
}

/** Parses a WKT "POINT(x y)" string into [x, y]. */
export function parsePoint(wkt: string): [number, number] {
  const match = wkt.match(/POINT\(([-\d.]+)\s+([-\d.]+)\)/)
  if (!match) throw new Error('Invalid point')
  return [Number(match[1]), Number(match[2])]
}

export function rectangleAround(lat: number, lng: number, widthM: number, depthM: number): LatLng[] {
  const dLat = depthM / 2 / M_PER_DEG_LAT
  const dLng = widthM / 2 / (Math.cos((lat * Math.PI) / 180) * M_PER_DEG_LNG_EQ)
  return [
    [lat - dLat, lng - dLng],
    [lat - dLat, lng + dLng],
    [lat + dLat, lng + dLng],
    [lat + dLat, lng - dLng],
  ]
}
